import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { intlShape, injectIntl } from 'react-intl';

import { Wizard, WizardPage } from '../../../common/components';
import { withSession } from '../../../i18n/containers/AppInit.js';
import { NoAttributes } from '../';
import { AssetEditFields, AssetTagFields } from '../';

import './AssetEditWizard.scss';

class AssetEditWizard extends Component {
  constructor(props) {
    super(props);
    this.state = { validation: [] };
    this.errors = {};
  }

  handleFinish(session) {
    return modal => {
      let { asset, mode, selectedParent } = this.data();
      asset.start_datetime =
        asset.start_datetime?.dateTimeStr || asset.start_datetime;
      asset.end_datetime =
        asset.end_datetime?.dateTimeStr || asset.end_datetime;
      delete asset.start_date;
      delete asset.end_date;
      switch (mode) {
        case 'create':
          this.props.actions.createAsset(
            { ...asset },
            {
              callback: () => {
                this.props.actions.getAssetTypes({
                  callback: () => {
                    if (selectedParent) {
                      this.props.actions.refreshAsset(selectedParent.uuid, {
                        context: 'browser'
                      });
                    }
                  }
                });
              },
              context: 'browser',
              cToken: session.cToken
            }
          );

          break;
        case 'edit':
          const { prevAsset, ...nextAsset } = asset;

          this.props.actions.editAsset(
            { ...nextAsset },
            {
              callback: () => {
                this.props.actions.getAssetTypes({
                  callback: () => {
                    if (asset.parent_id) {
                      // refresh the new parent
                      this.props.actions.refreshAsset(asset.parent_id, {
                        context: 'browser'
                      });
                      // refresh the old parent
                      if (
                        prevAsset.parent_id &&
                        selectedParent.uuid !== prevAsset.parent_id
                      ) {
                        this.props.actions.refreshAsset(selectedParent.uuid, {
                          context: 'browser'
                        });
                      }
                    }
                  }
                });
              },
              context: 'browser',
              cToken: session.cToken
            }
          );

          break;
        default:
          break;
      }
      this.props.actions.closeModal(modal);
      this.handleClose();
    };
  }

  standardFieldsChanged(prevAsset, nextAsset) {
    const standardFields = [
      'type',
      'url',
      'availability',
      'locale',
      'alt_text',
      'fallback',
      'parent_id',
      'subject_line',
      'preheader',
      'child_ids',
      'image_url',
      'content_builder_id'
    ];

    for (const field of standardFields) {
      if (prevAsset[field] !== nextAsset[field]) {
        return true;
      }
    }

    return false;
  }

  attributesChanged(prevAsset, nextAsset, attributes) {
    const attributeNames = attributes.map(attr => attr.name);
    const prevCustom = prevAsset.custom;
    const nextCustom = nextAsset.custom;

    for (const attrName of attributeNames) {
      if (prevCustom[attrName] !== nextCustom[attrName]) {
        return true;
      }
    }

    return false;
  }

  handleClose() {
    this.setState({ validation: [] });
    this.errors = {};
    this.props.actions.clearTags();
    this.props.actions.clearAssetSearchContext();
  }

  data() {
    return this.props.modals.assetWizard?.data || {};
  }

  selectedAssetType() {
    const type = this.data()?.asset.type;
    return this.props.assetTypes.filter(t => t.name === type)[0];
  }

  isChildAsset() {
    const selectedAssetType = this.selectedAssetType();
    if (selectedAssetType) {
      const parentAssetType = this.props.assetTypes.filter(
        t => t.uuid === selectedAssetType.parent_class_id
      )[0];
      return parentAssetType !== undefined;
    }

    return false;
  }

  imageFieldChange(element, value, fields, initializing) {
    const { asset, customFields } = this.data();
    element.value = value;

    const data = this.validateField(element);
    asset[element.field] = value;

    this.errors[element.field] = data.errorText;

    const flatten = fields.flat();
    const completed = flatten.every(field => {
      const empty = !(field.value && field.value.length);

      if (field.required && empty) {
        return false;
      } else if (
        field.hasError ||
        (field.errorText !== '' && field.errorText !== null)
      ) {
        return false;
      } else {
        return true;
      }
    });

    const update = {
      asset: { ...asset },
      errors: { ...this.errors },
      customFields,
      step0: {
        completed
      }
    };
    this.props.actions.updateModal('assetWizard', update);
  }

  imageFieldBlur(data, fields) {
    this.imageFieldChange(data, data.value, fields);
  }

  standardFieldChange(element, value, fields, initializing) {
    const { asset, customFields } = this.data();
    element.value = value;

    const data = this.validateField(element);

    if (value?.date) {
      if (asset[element.field]) {
        const timePart = asset[element.field].split('T')[1];
        asset[element.field] = `${value.date}T${timePart}`;
      } else {
        asset[element.field] = value.date;
      }
    } else if (value?.time) {
      const datePart = asset[element.field].split('T')[0];
      asset[element.field] = `${datePart}T${value.time}`;
    } else {
      asset[element.field] = value;
    }

    this.errors[element.field] = data.errorText;

    const flatten = fields.flat();
    const completed = flatten.every(field => {
      const empty = !(field.value && field.value.length);

      if (field.required && empty) {
        return false;
      } else if (
        field.hasError ||
        (field.errorText !== '' && field.errorText !== null)
      ) {
        return false;
      } else {
        return true;
      }
    });

    const update = {
      asset: { ...asset },
      errors: { ...this.errors },
      customFields,
      step2: {
        completed
      }
    };
    this.props.actions.updateModal('assetWizard', update);
  }

  standardFieldBlur(data, fields) {
    this.standardFieldChange(data, data.value, fields);
  }

  classFieldChange(element, value, fields) {
    const { asset, customFields } = this.data();
    element.value = value;

    const data = this.validateField(element);
    asset[element.field] = value;

    this.errors[element.field] = data.errorText;

    const isChild = this.isChildAsset();
    const flatten = fields.flat();
    const completed = flatten.every(field => {
      const empty = !(field.value && field.value.length);
      const required = field.required || (isChild && field.field === 'parent');
      if (required && empty) {
        return false;
      } else if (
        field.hasError ||
        (field.errorText !== undefined &&
          field.errorText !== '' &&
          field.errorText !== null)
      ) {
        return false;
      } else {
        return true;
      }
    });

    let update = {
      asset: { ...asset },
      errors: { ...this.errors },
      customFields,
      step1: {
        completed
      }
    };

    // remove parent selection if type (asset class) is changed
    if (element.field === 'type') {
      update.asset.parent = null;
      update.asset.parent_id = null;
      update.selectedParent = null;
    }

    this.props.actions.updateModal('assetWizard', update);
  }

  tagFieldChange(element, value) {
    const { asset } = this.data();
    const update = {
      asset: {
        ...asset,
        ect_tags: value
      }
    };
    this.props.actions.updateModal('assetWizard', update);
  }

  classFieldBlur(field) {
    const validation = [...new Set([...this.state.validation, field])];
    this.setState({ validation });
  }

  customFieldChange(element, value, fields) {
    const { asset, customFields } = this.data();
    const custom = asset.custom || {};
    element.value = value;

    const data = this.validateField(element);
    custom[element.field] = value;

    this.errors[element.field] = data.errorText;

    const flatten = fields.flat();
    const completed = flatten.every(field => {
      const empty = !(field.value && field.value.length);

      if (field.required && empty) {
        return false;
      } else if (
        field.hasError ||
        (field.errorText !== '' && field.errorText !== null)
      ) {
        return false;
      } else {
        return true;
      }
    });

    const update = {
      asset: {
        ...asset,
        custom: { ...custom }
      },
      errors: { ...this.errors },
      customFields,
      step3: {
        completed
      }
    };
    this.props.actions.updateModal('assetWizard', update);
  }

  validateField(data) {
    const { formatMessage } = this.props.intl;
    const missing = formatMessage({ id: 'components.ecs.assetWizard.missing' });

    const empty = !data.value?.length;

    let invalidMessage;
    switch (data.field) {
      case 'url':
        invalidMessage = formatMessage({
          id: 'components.ecs.assetWizard.invalidURL'
        });
        break;
      case 'image_url':
        invalidMessage = formatMessage({
          id: 'components.ecs.imageURLModal.invalid'
        });
        break;
      default:
        invalidMessage = formatMessage({
          id: 'components.ecs.assetWizard.invalidChoice'
        });
        break;
    }

    data.errorText = '';

    if (empty && data.required) {
      data.errorText = missing;
      data.hasError = true;
    } else if (data.maxLength && data.value?.length > data.maxLength) {
      data.errorText = formatMessage(
        { id: 'components.ecs.assetWizard.fieldTooLong' },
        { maxLength: data.maxLength }
      );
      data.hasError = true;
    } else if (
      data.hasOwnProperty('extraValidation') &&
      !data.extraValidation(data.value)
    ) {
      data.errorText = invalidMessage;
      data.hasError = true;
    }

    return data;
  }

  renderNoAttributes() {
    return <NoAttributes intl={this.props.intl} attributeType="asset" />;
  }

  pages() {
    const { mode, asset, errors, disabled } = this.data();
    const hasCustomAttributes =
      this.props.attributes.assetAttributes.length > 0;

    const imagePage = (
      <WizardPage
        key="step0"
        step="step0"
        required
        stepName="components.ecs.assetWizard.step0.progressIndicator"
        flex
        footer={['CANCEL', 'PROGRESS', 'NEXT']}
      >
        <AssetEditFields
          actions={this.props.actions}
          modals={this.props.modals}
          mode={mode}
          asset={asset}
          assets={this.props.assets}
          assetTypes={this.props.assetTypes}
          canDisableAsset={!disabled}
          contentBuilder={this.props.contentBuilder}
          type="image"
          validation={this.state.validation}
          onFieldBlur={this.imageFieldBlur.bind(this)}
          onFieldChange={this.imageFieldChange.bind(this)}
          errors={errors}
        />
      </WizardPage>
    );

    const nameAndClassPage = (
      <WizardPage
        key="step1"
        step="step1"
        required
        stepName="components.ecs.assetWizard.step1.progressIndicator"
        flex
        footer={['BACK', 'PROGRESS', 'NEXT']}
      >
        <AssetEditFields
          actions={this.props.actions}
          modals={this.props.modals}
          mode={mode}
          asset={asset}
          assets={this.props.assets}
          assetTypes={this.props.assetTypes}
          contentBuilder={this.props.contentBuilder}
          type="class"
          validation={this.state.validation}
          isChildAsset={this.isChildAsset()}
          onFieldBlur={this.classFieldBlur.bind(this)}
          onFieldChange={this.classFieldChange.bind(this)}
          errors={errors}
        />
      </WizardPage>
    );

    const standardFieldsPage = (
      <WizardPage
        key="step2"
        step="step2"
        required
        stepName="components.ecs.assetWizard.step2.progressIndicator"
        flex
        footer={['BACK', 'PROGRESS', 'NEXT']}
      >
        <AssetEditFields
          modals={this.props.modals}
          mode={mode}
          asset={asset}
          assetTypes={this.props.assetTypes}
          contentBuilder={this.props.contentBuilder}
          canDisableAsset={!disabled}
          type="standard"
          rules={this.props.rules}
          validation={this.state.validation}
          onFieldBlur={this.standardFieldBlur.bind(this)}
          onFieldChange={this.standardFieldChange.bind(this)}
          errors={errors}
        />
      </WizardPage>
    );

    const assetClass = this.props.assetTypes.find(ac => {
      if (asset.asset_class_id) {
        return ac.uuid === asset.asset_class_id;
      } else {
        return ac.name === asset.type;
      }
    });
    let weatherEnabled =
      this.props.rules?.settings?.weather && assetClass?.weather_aware;
    const hasWeatherAttributes =
      weatherEnabled && this.props.attributes.weatherAttributes.length > 0;
    const taggingEnabled = this.props.rules?.settings?.ect;

    let lastStep = 'custom';
    if (taggingEnabled) {
      lastStep = 'tags';
    } else if (weatherEnabled) {
      lastStep = 'weather';
    }
    const nextOrFinish = step => (lastStep === step ? 'FINISH' : 'NEXT');

    const customFieldsPage = (
      <WizardPage
        key="step3"
        step="step3"
        required={hasCustomAttributes}
        stepName="components.ecs.assetWizard.step3.progressIndicator"
        flex
        footer={['BACK', 'PROGRESS', nextOrFinish('custom')]}
      >
        {!hasCustomAttributes && (!weatherEnabled || !hasWeatherAttributes) ? (
          this.renderNoAttributes()
        ) : (
          <AssetEditFields
            modals={this.props.modals}
            mode={mode}
            asset={asset}
            attributes={this.props.attributes.assetAttributes}
            contentBuilder={this.props.contentBuilder}
            type="custom"
            onFieldChange={this.customFieldChange.bind(this)}
            errors={errors}
          />
        )}
      </WizardPage>
    );

    let pages = [imagePage, nameAndClassPage, standardFieldsPage];

    if (hasCustomAttributes || (!hasCustomAttributes && !weatherEnabled)) {
      pages.push(customFieldsPage);
    }

    if (weatherEnabled) {
      const weatherAttributes = Object.entries(this.props.attributes).flatMap(
        entry => {
          if (entry[0].toLowerCase().startsWith('weather')) {
            return entry[1];
          } else {
            return [];
          }
        }
      );
      pages.push(
        <WizardPage
          key="step4"
          step="step4"
          required={false}
          stepName="components.ecs.assetWizard.step4.progressIndicator"
          flex
          footer={['BACK', 'PROGRESS', nextOrFinish('weather')]}
        >
          <AssetEditFields
            modals={this.props.modals}
            mode={mode}
            asset={asset}
            attributes={weatherAttributes}
            contentBuilder={this.props.contentBuilder}
            type="weather"
            onFieldChange={this.customFieldChange.bind(this)}
            errors={errors}
          />
        </WizardPage>
      );
    }

    if (taggingEnabled) {
      pages.push(
        <WizardPage
          key="step5"
          step="step5"
          required={false}
          stepName="components.ecs.assetWizard.step5.progressIndicator"
          flex
          footer={['BACK', 'PROGRESS', nextOrFinish('tags')]}
        >
          <AssetTagFields
            actions={this.props.actions}
            mode={mode}
            asset={asset}
            tags={this.props.tags}
            type="tags"
            onFieldChange={this.tagFieldChange.bind(this)}
            errors={errors}
          />
        </WizardPage>
      );
    }
    return pages;
  }

  render() {
    if (!this.data()?.asset) {
      return null;
    }
    const { mode } = this.data();
    const { formatMessage } = this.props.intl;

    const title = formatMessage({
      id: `components.ecs.assetWizard.titles.${mode}`
    });
    return withSession(session => {
      return (
        <Wizard
          title={title}
          modalName="assetWizard"
          containerClassName="asset-wizard"
          size="small"
          onFinish={this.handleFinish(session)}
          onClose={this.handleClose.bind(this)}
          finishLabel={formatMessage({ id: 'common.actions.save' })}
          {...this.props}
        >
          {this.pages()}
        </Wizard>
      );
    });
  }
}

AssetEditWizard.propTypes = {
  actions: PropTypes.shape({
    createTags: PropTypes.func.isRequired,
    closeModal: PropTypes.func.isRequired,
    updateModal: PropTypes.func.isRequired
  }).isRequired,
  assets: PropTypes.object,
  assetTypes: PropTypes.array,
  attributes: PropTypes.object,
  contentBuilder: PropTypes.object,
  modals: PropTypes.shape({
    assetWizard: PropTypes.shape({
      data: PropTypes.shape({
        mode: PropTypes.oneOf(['create', 'edit']).isRequired,
        asset: PropTypes.object.isRequired,
        customFields: PropTypes.array.isRequired,
        errors: PropTypes.object
      })
    })
  }).isRequired,
  rules: PropTypes.object,
  tags: PropTypes.object,
  intl: intlShape.isRequired
};

export default injectIntl(AssetEditWizard);
